import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ActionItem, ModalActionEnum } from '@dialogs/actions-panel/actions-panel.component';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { GridOverrides, RowSelection } from '@mds/common';
import { MdsIcon } from '@mds/icons';
import {
  ButtonComponent,
  GridActionsComponent,
  GridCheckboxButtonComponent,
  GridModule,
  GridNoRowsComponent
} from '@mds/ui';
import { NewUiRoute } from '@mds/common';
import { ComposerService } from '@services/composer.service';
import { ToastrService } from '@services/toaster.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { numericComparator } from '@utils/numeric-comparator';
import { sortBy } from '@utils/sort-by';
import { textComparator } from '@utils/text-comparator';
import { GridApi } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { MakersiteService } from '@services/makersite.service';
import { cloneDeep } from 'lodash';
import { forkJoin, Observable } from 'rxjs';

interface Actions {
  edit: boolean;
  delete: boolean;
  view: boolean;
}

interface DataRow {
  key: string;
  label: string;
  sortlevel?: number;
  value: any;
  link?: boolean;
}

interface RowItem {
  key: string;
  label: string;
  value: any;
  display_value: string;
  link?: boolean;
  rid?: string;
}

interface FormSchemaItem {
  key: string;
  formtype: string;
  fieldtype: string;
  enum?: Array<{ name: string; value: any }>;
}

const NUMERIC_FIELD_TYPES = new Set(['FLOAT', 'float', 'number', 'NUMBER']);
const NO_FILTER_TEXT_FIELDS = new Set(['type_label', 'Unit', 'unit']);
const NO_EDIT_FIELDS = new Set(['out', 'in', 'is_private', 'preferred_route']);

const SUBSTITUTION_ALTERNATIVES_TAB = 'Substitution Alternatives';

@Component({
  selector: 'app-composer-grid',
  standalone: true,
  imports: [GridModule],
  templateUrl: './composer-grid.component.html',
  providers: [MakersiteService],
  styleUrls: ['./composer-grid.component.scss']
})
export class ComposerGridComponent implements OnChanges, OnDestroy {
  overrides: GridOverrides;
  availableActions: ActionItem[] = [];
  @Input() tabData: any;
  @Output() reloadData = new EventEmitter<void>();
  @Output() selectedRow = new EventEmitter<any>();
  @Output() actionToPerform = new EventEmitter<ActionItem[]>();

  rowData: any[] = [];
  data: any;
  loadNewPage: boolean;
  selectedRows: any[] = [];
  inlineEditRow: any[] = [];
  selectedRowData: any;
  pageNo: number;
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private gridApi: GridApi;

  constructor(
    private makersiteService: MakersiteService,
    private toastrService: ToastrService,
    private toolbarEventService: ToolbarEventService,
    private composerService: ComposerService,
    private router: Router
  ) {}

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;
    if (this.gridApi) {
      setTimeout(() => {
        gridApi?.resetRowHeights();
        gridApi?.refreshHeader();
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.tabData) {
      return;
    }
    this.data = cloneDeep(this.tabData);
    this.overrides = undefined;
    this.init();
    if (changes?.data?.previousValue?.name !== changes?.data?.currentValue?.name) {
      this.selectedRows = [];
    }
  }

  revert(list): Observable<any> {
    const allRes = [];
    list.forEach((rid) => {
      const response = this.makersiteService.restoreItem(rid);
      allRes.push(response);
    });
    return forkJoin(allRes);
  }

  onSelectionChanged(): void {
    this.selectedRows = this.gridApi.getSelectedRows();
    if (this.selectedRows.length > 0) {
      const deleteAction: ActionItem = {
        label: 'Delete',
        icon: MdsIcon.PROFILE_TRASH,
        value: ModalActionEnum.DELETE,
        callback: () => this.deleteConfirmation()
      };

      if (this.availableActions.length === 1) {
        this.availableActions.unshift(deleteAction);
        this.actionToPerform.emit(this.availableActions);
      }
    } else if (this.selectedRows.length < 1) {
      this.activateActionModal();
    }
  }

  toggleCheckbox(params) {
    const currentField = params.colDef.field;
    const newVal = !params.data[currentField];
    params.node.setDataValue(currentField, newVal);
  }

  saveChangesWarningDialog(rowData) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Warning',
        text: 'It looks like you have been editing something. Please save; otherwise, your changes will be lost.',
        cancel_text: 'Cancel',
        delete_text: 'Continue'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.composerService.hasUnsavedChanges = false;
        this.updateGridData(rowData);
      }
    });
    return dialogRef.afterClosed();
  }

  updateMenuActions(obj) {
    this.toolbarEventService.updateMegaMenuActions.next(obj);
  }

  onCellClicked(ev: any): void {
    if (!ev.data) return;

    if ((ev.data[`Link_${ev.column.colId}`] || ev.data[`Link_${ev.colDef.headerName}`]) && !ev.data.isEditing) {
      const link = ev.data[`Link_${ev.column.colId}`] || ev.data[`Link_${ev.colDef.headerName}`];
      const newUiRoute = new NewUiRoute();
      const updatedUrl = newUiRoute.transform(link.url, true);

      window.open(updatedUrl, '_blank');
    }

    this.selectedRowData = ev.data;

    this.selectedRow.emit(this.selectedRowData);
  }

  onCellValueChanged(ev) {
    if (ev.event?.newValue === ev.event?.oldValue) {
      return;
    }
    const { colId } = ev.event.column;
    const tempId = ev.data.rid || ev.data.in || ev.data.out;

    if (!this.inlineEditRow.includes(tempId)) {
      ev.data.edited = true;
      this.inlineEditRow.push(tempId);
    }

    if (this.inlineEditRow.length > 0) {
      this.composerService.hasUnsavedChanges = true;
      this.updateMenuActions({ 'save-grid': true });
    } else {
      this.composerService.hasUnsavedChanges = false;
      this.updateMenuActions({ 'save-grid': true, hidden: true });
    }

    ev.data.row.forEach((d) => {
      d.key = d.key.replace('$', '.');
      let key = d.key.split('.');
      key = key.pop();
      if (key === colId) {
        d.value = ev.event.newValue;
        d.display_value = ev.event.newValue;
        if (!ev.data?.editedCell.includes(key)) {
          ev.data.editedCell.push(key);
        }
      }
    });
  }

  /**
   * this method is used for pagination in composer grid
   */
  getNextPageData() {
    this.pageNo++;
    const edge = this.data.lines[0].find((e) => e.key.endsWith('.rid'));
    const subModule = edge.rid || edge.value;
    this.makersiteService.showAll(this.data.targetRid, subModule, this.pageNo).subscribe((res: any): void => {
      if (res.lines?.length > 0) {
        this.loadNewPage = true;
        this.reloadComposer();
      }
    });
  }

  ngOnDestroy(): void {
    this.dialog.closeAll();
  }

  private init() {
    this.createGridData();
  }

  private createGridData() {
    this.rowData = this.getRows(this.data?.lines);
    const headerData = this.getHeaders();

    this.loadNewPage = true;
    this.overrides = {
      columnDefs: headerData,
      rowData: this.rowData,
      defaultColDef: {
        headerCheckboxSelectionFilteredOnly: true,
        lockPosition: true,
        lockPinned: true,
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        flex: 1
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel'
          }
        ]
      },
      suppressRowClickSelection: true,
      rowSelection: RowSelection.MULTIPLE,
      noRowsOverlayComponent: GridNoRowsComponent,
      paginationAutoPageSize: true,
      pagination: true,
      noRowsOverlayComponentParams: {
        title: 'No data to show the table',
        description: 'Add data and it will start showing rows here'
      }
    };
    this.dialog.closeAll();
    this.activateActionModal();
  }

  private getHeaders() {
    const isEditable = true;
    const rawHeader = this.data.lines[0] || [];
    const ordered = rawHeader.filter((r) => r.sortlevel);
    sortBy(ordered);
    const unordered = rawHeader.filter((r) => !r.sortlevel);
    const headerData = [...ordered, ...unordered];
    const cols = headerData.filter((r) => r.key && !r.key.endsWith('.rid')).map((r) => this.buildColDef(r));

    if (cols.length && isEditable) {
      this.addSelectionAndActionCols(cols);
    }

    return cols;
  }

  private buildColDef(row: DataRow): ColDef {
    const rawKey = row.key.replace('$', '.');
    const field = rawKey.split('.').pop()!;
    const col: ColDef = {
      field,
      headerName: row.label,
      comparator: textComparator,
      minWidth: 150,
      sortable: false,
      filter: 'agTextColumnFilter',
      enableRowGroup: row.label !== 'Main Output'
    };
    if (field === 'is_private') {
      col.cellRenderer = 'TogglePrivateRendererComponent';
      delete col.filter;
    }

    if (['in', 'out', 'name'].includes(field)) {
      col.enablePivot = true;
      col.pivot = true;
    }

    if (row.label === 'Main Output' && field === 'outputGp') {
      col.cellRenderer = GridCheckboxButtonComponent;
    }

    if (typeof row.value === 'number') {
      col.filter = 'agNumberColumnFilter';
      col.aggFunc = 'sum';
      col.enableValue = true;
      col.comparator = numericComparator;
      col.sortable = true;
    } else if (['string', 'object'].includes(typeof row.value)) {
      if (!NO_FILTER_TEXT_FIELDS.has(field)) {
        col.filter = 'agTextColumnFilter';
      }
      col.sortable = true;
    } else {
      col.sortable = true;
    }

    if (this.data.actions.edit && this.data.formSchema) {
      this.applyFormSchema(col, field);
    }

    if (row.link) {
      const headerName = row.label;
      col.cellStyle = (params) => {
        const hasLink = params.data?.[`Link_${headerName}`] || params.data?.[`Link_${params.colDef.field}`];
        return hasLink
          ? { color: '#1960e2', backgroundColor: '#f5efef', cursor: 'pointer' }
          : { backgroundColor: '#f5efef' };
      };
      col.valueFormatter = (params) => {
        const hasLink = params.data?.[`Link_${headerName}`] || params.data?.[`Link_${params.colDef.field}`];
        if (!hasLink && params.value === 'Unknown') {
          return `No ${headerName}`;
        }
        return params.value;
      };
    } else if (col.editable) {
      col.cellStyle = { backgroundColor: '#f5efef' };
    }
    return col;
  }

  private applyFormSchema(col: ColDef, field: string) {
    const isEditable = false;
    this.data.formSchema!.forEach((form) => {
      const formKey = form.key.replace('$', '.').split('.').pop()!;
      if (formKey !== field) return;
      let canEditField = !NO_EDIT_FIELDS.has(field) && !['display_only', 'checkbox', 'select'].includes(form.formtype);
      // form.formtype !== 'display_only';

      if (
        formKey === field &&
        form.section === 'Synonyms' &&
        form.fieldtype === 'LINK' &&
        form.formtype !== 'display_only'
      ) {
        canEditField = true;
      }

      if (canEditField) {
        col.editable = isEditable;
      }

      // select editors
      if (['select', 'select_boolean'].includes(form.formtype)) {
        col.editable = isEditable;
        col.cellEditorSelector = () => ({
          component: 'agRichSelectCellEditor',
          params: { values: form.enum?.map((e) => e.name) || [] }
        });
      }

      if (form.formtype === 'checkbox' && col.field !== 'is_private') {
        col.onCellClicked = this.toggleCheckbox;
      }

      if (NUMERIC_FIELD_TYPES.has(form.fieldtype)) {
        col.editable = isEditable;
        col.cellEditor = 'NumericEditorComponent';
        col.filter = 'agNumberColumnFilter';
        col.aggFunc = 'sum';
        col.comparator = numericComparator;
      } else if (['tile-autocomplete', 'tile-autoselect'].includes(form.formtype)) {
        Object.assign(col, {
          editable: isEditable,
          cellEditor: 'TileAutocompleteCellEditorComponent',
          getValue: this.objValueGetter,
          valueFormatter: this.objValueFormatter,
          cellStyle: { backgroundColor: '#f5efef' },
          minWidth: 250
        });
      } else if (['autocomplete', 'autoselect'].includes(form.formtype)) {
        Object.assign(col, {
          editable: isEditable,
          cellEditor: 'AutocompleteCellEditorComponent',
          getValue: this.objValueGetter,
          valueFormatter: this.objValueFormatter,
          cellStyle: { backgroundColor: '#f5efef' },
          minWidth: 250
        });
      } else if (form.formtype === 'date') {
        Object.assign(col, {
          editable: false,
          cellStyle: { backgroundColor: '#f5efef' },
          cellRenderer: 'DateCellEditorComponent'
        });
      } else if (form.formtype === 'formula') {
        col.cellEditor = 'FormulaEditorCellComponent';
      } else if (form.formtype === 'checkbox' && !form.key.endsWith('is_private')) {
        Object.assign(col, {
          cellStyle: { backgroundColor: '#f5efef' },
          cellRenderer: GridCheckboxButtonComponent
        });
      } else if (form.formtype === 'hidden') {
        col.hide = true;
        col.editable = false;
      }
    });
  }

  private addSelectionAndActionCols(cols: ColDef[]) {
    cols.push({
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: GridActionsComponent,
      cellRendererParams: {
        onClick: (params: any) => this.singleRowEvent(params)
      },
      width: 140,
      pinned: 'right',
      filter: false,
      sortable: false,
      lockPinned: true,
      lockPosition: 'right',
      suppressSizeToFit: true,
      resizable: false
      // maintainColumnOrder: true
    });
    cols.push({
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      pinned: 'left',
      lockPosition: 'right',
      lockPinned: true,
      width: 48,
      filter: false,
      sortable: false,
      suppressSizeToFit: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
    });
  }

  private getRows(lines: any[]) {
    const selectBooleanMap = this.buildSelectBooleanMap();

    return lines.map((rowItems) => this.buildRowData(rowItems, selectBooleanMap));
  }

  private buildSelectBooleanMap() {
    return this.data.formSchema
      .filter((f) => f.formtype === 'select_boolean')
      .reduce((map, form) => {
        const key = this.normalizeKey(form.key);
        map[key] = form;
        return map;
      }, {});
  }

  private buildRowData(items: RowItem[], selectBoolMap: Record<string, FormSchemaItem>) {
    const rowData: any = {
      row: items,
      actions: {
        edit: this.data.actions.edit,
        delete: this.data.actions.delete,
        view: this.data.actions.view
      },
      formSchema: this.data.formSchema,
      editedCell: []
    };

    for (const item of items) {
      const field = this.normalizeKey(item.key);

      // link columns get special field name
      if (item.link) {
        rowData[`Link_${item.label}`] = item.link;
      }

      // choose which value to assign
      let cellValue: any;
      if ('closest_match_fit_score'.includes(field)) {
        if (item.display_value === 'Unknown' || item.value == null) {
          cellValue = '';
        } else {
          cellValue = item.display_value;
        }
      } else if (['is_private', 'outputGp', 'rid'].includes(field)) {
        cellValue = item.rid ?? item.value;
      } else if (['valid_from', 'valid_to', 'created_on'].includes(field)) {
        cellValue = this.extractDate(item.display_value);
      } else if (selectBoolMap[field]) {
        cellValue = this.mapSelectBoolean(item.value, selectBoolMap[field].enum);
      } else if (typeof item.value === 'number') {
        cellValue = item.value;
      } else {
        cellValue = item.display_value;
      }

      rowData[field] = cellValue;
    }

    const completeRow = rowData;
    completeRow.originalData = cloneDeep(completeRow);
    return completeRow;
  }

  private objValueFormatter(params: any) {
    if (!params.value) return '';
    return params.value.title || params.value.name || params.value;
  }

  private objValueGetter(params: any) {
    if (!params.value) return '';
    return params.value.value || params.value;
  }

  private normalizeKey(raw: string): string {
    return raw.replace('$', '.').split('.').pop()!;
  }

  private extractDate(displayValue: string): string {
    return displayValue.split(' ')[0];
  }

  private mapSelectBoolean(rawVal: any, enumList?: Array<{ name: string; value: any }>): string {
    // cast booleans to strings
    const val = typeof rawVal === 'boolean' ? String(rawVal) : rawVal;

    if (!enumList) return val;
    const match = enumList.find((e) => e.value === val);
    return match ? match.name : val;
  }

  private singleRowEvent(params: { event: ModalActionEnum; node: any }) {
    if (params.event === ModalActionEnum.DELETE) {
      this.openDeleteRowConfirmation(params);
    } else if (params.event === ModalActionEnum.EDIT) {
      this.editRow(params);
    } else if (params.event === ModalActionEnum.VIEW) {
      this.viewActions(params.node.data.row);
    }
  }

  private activateActionModal(): void {
    this.availableActions = [];

    if (this.tabData.actions.new) {
      const ADD_ACTION: ActionItem = {
        label: `Add ${this.tabData.name ?? ''}`,
        icon: MdsIcon.PLUS_DARK,
        value: ModalActionEnum.ADD,
        callback: () => this.addNewDataSet()
      };
      this.availableActions.push(ADD_ACTION);
    }

    this.actionToPerform.emit(this.availableActions);
  }

  private deleteConfirmation() {
    const deletedRids = this.selectedRows.map((e) => e.rid);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Confirm',
        header: `Delete${deletedRids.length > 1 ? ' Items?' : ' Item?'}`,
        text: `${deletedRids.length + (deletedRids.length > 1 ? ' Items' : ' Item')} will be deleted. You can still restore the data from the Trash.`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        const targetRid = this.tabData.name === SUBSTITUTION_ALTERNATIVES_TAB
          ? this.data.targetRid.replace('#', '')
          : null;

        this.makersiteService.deleteMultipleDataset(deletedRids, targetRid).subscribe((res) => {
          this.selectedRows = [];
          this.availableActions.shift();
          this.actionToPerform.emit(this.availableActions);

          this.toastrService
            .showWithAction('Deleted Successfully', 'Undo', 5000)
            .afterDismissed()
            .subscribe((restore) => {
              if (!restore.dismissedByAction) return;
              const trash = Object.values(res);
              const trashIds = [];
              trash.forEach((e: any) => {
                if (e.message === 'success') {
                  trashIds.push(e.trash_rids[0]);
                }
              });
              this.revert(trashIds).subscribe((revert) => {
                this.reloadComposer();
              });
            });
          this.reloadComposer();
        });
      }
    });
  }

  private reloadComposer() {
    this.reloadData.emit();
  }

  private openDeleteRowConfirmation(ev: any) {
    const { data } = ev.node;
    const lines = data.row;
    let deletedIndex: number;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Confirm',
        header: 'Delete Item?',
        text: 'Item will be deleted. You can still restore the data from the Trash',
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (!confirm) return;
      let rid = '';
      lines.forEach((row: any) => {
        if (row.key.endsWith('.rid') || row.key.endsWith('$rid')) {
          rid = row.rid;
        }
      });
      if (!rid) return;
      const payload =
        this.tabData.name === SUBSTITUTION_ALTERNATIVES_TAB
          ? { rid: this.data.targetRid.replace('#', '') }
          : null;

      this.makersiteService.deleteData(rid, payload).subscribe((res) => {
        const trash = res[`#${rid}`];
        const trashIds = trash.trash_rids;
        if (this.inlineEditRow.includes(rid)) {
          this.inlineEditRow = this.inlineEditRow.filter((e) => e !== rid);
          if (this.inlineEditRow.length === 0) {
            this.composerService.hasUnsavedChanges = false;
            this.updateMenuActions({ 'save-grid': true, hidden: true });
          }
        }

        // removing deleted row from main composer tab data
        const updatedLines = this.data.lines.filter((line) => {
          const edge = line.find((e) => e.key.endsWith('.rid'));
          const edgeRid = edge?.rid || edge?.value;
          return edgeRid !== rid;
        });
        this.data.lines = [...updatedLines];
        if (trash.message === 'success') {
          this.toastrService
            .showWithAction('Deleted Successfully', 'Undo', 5000)
            .afterDismissed()
            .subscribe((restore) => {
              if (!restore.dismissedByAction) return;
              this.makersiteService.restoreItem(trashIds[0]).subscribe((res: { message: string }) => {
                this.toastrService.showMessage('Restored successfully.');
                this.reloadComposer();
              });
            });
        }
        this.reloadComposer();
        for (const i in this.rowData) {
          if (this.rowData[i] === data) {
            deletedIndex = Number(i);
            this.reloadComposer();
          }
        }
      });
    });
  }

  private editRow(ev: any) {
    if (ev.node.data.edited) {
      this.saveChangesWarningDialog(ev.node.data.originalData.row);
    } else {
      this.updateGridData(ev.node.data.row);
    }
  }

  private addNewDataSet() {
    // same as edit only lines array send empty to inline dialog
    this.updateGridData([]);
  }

  private updateGridData(lines: any[]) {
    const schema = {
      formSchema: this.data.formSchema,
      lines: lines || [],
      rid: this.data.targetRid,
      layer: this.data.layer,
      disableSubmit: true
    };
    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      width: '50vw',
      data: schema,
      autoFocus: false
    });
    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data && data.value.Inputs) {
        const payload = data.updatedValues || data.value.Inputs;

        const addNestedKeysToPayload = (keys: string[], value: any) => {
          let current = payload;

          keys.forEach((key) => {
            if (!(key in current)) {
              current[key] = value;
            }
            current = current[key];
          });
        };

        const regex = /^#\d+:\d+$/;
        const findRids = (obj, parents: string[] = []) => {
          Object.keys(obj).forEach((k) => {
            const val = obj[k];
            const valType = typeof val;
            const allParents = [...parents, k];

            if (val !== null && valType === 'object') {
              findRids(val, allParents);
            } else if (valType === 'string') {
              const isRid = regex.test(val);

              if (isRid) {
                addNestedKeysToPayload(allParents, val);
              }
            }
          });
        };

        findRids(data.value.Inputs);

        let isFileUpload = false;
        let uploaderCount = 0;

        this.data.formSchema.forEach((e) => {
          if (['attachments', 'image'].includes(e.formtype)) {
            const arrKey = e.key.split('.');
            const key = arrKey.pop();
            if (payload[arrKey[0]][key] && payload[arrKey[0]][key][0]) {
              isFileUpload = true;
              uploaderCount++;
              this.makersiteService.newFileUpload(this.data.targetRid, payload[arrKey[0]][key][0]).subscribe(
                (response) => {
                  payload[arrKey[0]][key] = Object.values(response)[0].file_rid;
                  uploaderCount--;
                  if (uploaderCount === 0) {
                    this.saveGridLine(lines, payload, dialogRef);
                  }
                },
                (err) => {
                  uploaderCount--;
                  if (uploaderCount === 0) {
                    this.saveGridLine(lines, payload, dialogRef);
                  }
                }
              );
            } else if (payload[arrKey[0]][key] && !payload[arrKey[0]][key][0]) {
              payload[arrKey[0]][key] = payload[arrKey[0]][key][0].rid;
            }
          }
        });
        if (!isFileUpload) {
          this.saveGridLine(lines, payload, dialogRef);
        }
      }
    });

    instance.changeDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data?.valid && data?.updatedValues) {
        dialogRef.componentInstance.data.disableSubmit = false;
      }
    });
  }

  private saveGridLine(lines: any[], payload: any, dialogRef: MatDialogRef<InlineFormSchemaDialogComponent>) {
    dialogRef.componentInstance.data.disableSubmit = true;

    let rid;
    lines.forEach((row) => {
      if (row.key.endsWith('.rid') || row.key.endsWith('$rid')) {
        rid = row.rid;
      }
    });
    const data: any = Object.values(payload)[0];
    if (rid) {
      data.rid = rid;
    }
    this.makersiteService.submitEditedMetadata(this.data.targetRid, payload).subscribe((res: any) => {
      if (res.updated) {
        dialogRef?.close();
        this.reloadComposer();
      } else if (res?.fails?.length) {
        dialogRef.componentInstance.data.disableSubmit = false;
        this.toastrService.showMessage((res?.fails || []).map((f) => f.message) ?? 'Something went wrong');
      }
    });
  }

  private viewActions(lines: any[]): void {
    lines.forEach((row) => {
      let key = row.key.replace('$', '.');
      key = key.split('.');
      key = key.pop();
      if (['in', 'out', 'name'].includes(key)) {
        const { rid } = row;
        this.router.navigate(['/mattermaps/overview/', rid, 'edit']);
      }
    });
  }
}
